import { VoiceSettings, VoiceEvent } from '../types';
import API_BASE_URL from '../config/api';
import yandexSpeechService from './yandexSpeechService';

// Токен авторизации для API запросов
let authToken: string | null = null;

export const setAuthToken = (token: string | null) => {
  authToken = token;
  if (yandexSpeechService && typeof yandexSpeechService.setAuthToken === 'function') {
    yandexSpeechService.setAuthToken(token);
  }
};

// Типы для Web Speech API
declare global {
  interface Window {
    webkitSpeechRecognition: any;
    SpeechRecognition: any;
  }
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  serviceURI: string;
  grammars: any;
  start(): void;
  stop(): void;
  abort(): void;
  onaudiostart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onaudioend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
  onnomatch: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onsoundstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onsoundend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
}

class VoiceService {
  private recognition: SpeechRecognition | null = null;
  private synthesis: SpeechSynthesisUtterance | null = null;
  private currentAudio: HTMLAudioElement | null = null; // Для Yandex TTS
  private currentAudioUrl: string | null = null; // URL для очистки
  private isListening = false;
  private isSpeaking = false;
  private eventListeners: Map<string, ((event: VoiceEvent) => void)[]> = new Map();
  private defaultSettings: VoiceSettings = {
    language: 'ru-RU',
    voice: null,
    pitch: 1,
    rate: 1,
    volume: 1,
    ttsEngine: 'browser',  // По умолчанию используем браузерный TTS
    autoSpeak: false,      // Автоозвучка отключена по умолчанию
    yandexVoice: 'alena',
    yandexEmotion: 'neutral',
    yandexSpeed: 1.0,
  };

  constructor() {
    this.initSpeechRecognition();
    this.loadSettings();
    this.logAvailableVoices();
  }

  /**
   * Логирование доступных голосов для отладки
   */
  private logAvailableVoices(): void {
    if ('speechSynthesis' in window) {
      // Голоса могут загружаться асинхронно
      const logVoices = () => {
        const voices = speechSynthesis.getVoices();
        console.log(`🎤 Доступно голосов: ${voices.length}`);
        const russianVoices = voices.filter(v => v.lang.startsWith('ru'));
        console.log(`🇷🇺 Русских голосов: ${russianVoices.length}`);
        if (russianVoices.length > 0) {
          console.log('Список русских голосов:', russianVoices.map(v => `${v.name} (${v.lang})`));
        }
      };

      // Вызываем сразу
      logVoices();

      // И при загрузке голосов (для некоторых браузеров)
      speechSynthesis.addEventListener('voiceschanged', logVoices, { once: true });
    }
  }

  /**
   * Инициализация распознавания речи
   */
  private initSpeechRecognition(): void {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      this.recognition = new SpeechRecognition();
      if (this.recognition) {
        this.recognition.continuous = false;
        this.recognition.interimResults = true;
        this.recognition.lang = this.defaultSettings.language;
        this.recognition.maxAlternatives = 1;
      }
      
      this.setupRecognitionEvents();
    } else {
      console.warn('Speech recognition not supported in this browser');
    }
  }

  /**
   * Настройка событий распознавания речи
   */
  private setupRecognitionEvents(): void {
    if (!this.recognition) return;

    this.recognition.onstart = () => {
      this.isListening = true;
      this.emitEvent({
        type: 'start',
        data: { action: 'listening' },
        timestamp: new Date()
      });
    };

    this.recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = '';
      let interimTranscript = '';

      try {
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result && result.length > 0) {
            const transcript = result[0]?.transcript || '';
            if (result.isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript + ' ';
            }
          }
        }

        // Убираем лишние пробелы
        finalTranscript = finalTranscript.trim();
        interimTranscript = interimTranscript.trim();

        // Логируем промежуточные результаты для отладки
        if (interimTranscript) {
          console.log('Промежуточное распознавание:', interimTranscript);
        }
        
        if (finalTranscript) {
          console.log('Финальное распознавание:', finalTranscript);
        }

        this.emitEvent({
          type: 'result',
          data: { finalTranscript, interimTranscript },
          timestamp: new Date()
        });
      } catch (error) {
        console.error('Ошибка при обработке результатов распознавания:', error);
        this.emitEvent({
          type: 'error',
          data: { 
            error: 'result_processing_error', 
            message: 'Ошибка при обработке результатов распознавания речи' 
          },
          timestamp: new Date()
        });
      }
    };

    this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      this.isListening = false;
      
      // Получаем более информативное сообщение об ошибке
      const errorCode = event.error || 'unknown';
      let errorMessage = 'Unknown error';
      
      switch (errorCode) {
        case 'no-speech':
          errorMessage = 'Речь не обнаружена. Пожалуйста, говорите громче.';
          break;
        case 'audio-capture':
          errorMessage = 'Микрофон не найден или недоступен. Проверьте подключение микрофона.';
          break;
        case 'not-allowed':
          errorMessage = 'Доступ к микрофону запрещен. Разрешите использование микрофона в настройках браузера.';
          break;
        case 'network':
          errorMessage = 'Ошибка сети при распознавании речи. Проверьте подключение к интернету.';
          break;
        case 'aborted':
          errorMessage = 'Распознавание речи прервано.';
          break;
        case 'service-not-allowed':
          errorMessage = 'Служба распознавания речи недоступна. Попробуйте позже.';
          break;
        case 'bad-grammar':
          errorMessage = 'Ошибка грамматики при распознавании.';
          break;
        case 'language-not-supported':
          errorMessage = 'Язык распознавания не поддерживается.';
          break;
        default:
          errorMessage = event.message || `Ошибка распознавания речи: ${errorCode}`;
      }
      
      console.error('Ошибка распознавания речи:', {
        error: errorCode,
        message: errorMessage,
        originalEvent: event
      });
      
      this.emitEvent({
        type: 'error',
        data: { error: errorCode, message: errorMessage },
        timestamp: new Date()
      });
    };

    this.recognition.onend = () => {
      console.log('Распознавание речи завершено');
      this.isListening = false;
      this.emitEvent({
        type: 'end',
        data: { action: 'listening' },
        timestamp: new Date()
      });
    };
  }

  /**
   * Начать прослушивание речи
   * Использует Yandex SpeechKit через серверный API
   */
  async startListening(): Promise<string> {
    // Используем Yandex SpeechKit как основной метод
    if (yandexSpeechService.isSupported()) {
      try {
        this.isListening = true;
        this.emitEvent({
          type: 'start',
          data: { action: 'listening' },
          timestamp: new Date()
        });

        // Начинаем запись
        await yandexSpeechService.startRecording({
          language: this.defaultSettings.language,
          format: 'oggopus',
          sampleRateHertz: 16000
        });

        // Ждем завершения записи и распознавания
        // Пользователь должен вызвать stopListening() для завершения
        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Таймаут ожидания распознавания. Нажмите кнопку микрофона еще раз для остановки.'));
          }, 60000); // 60 секунд максимум

          // Сохраняем resolve/reject для использования в stopListening
          (this as any)._recognitionResolve = (text: string) => {
            clearTimeout(timeout);
            resolve(text);
          };
          (this as any)._recognitionReject = (error: Error) => {
            clearTimeout(timeout);
            reject(error);
          };
        });
      } catch (error) {
        this.isListening = false;
        throw error;
      }
    }

    // Fallback на Web Speech API если Yandex недоступен
    return this.startListeningWebSpeech();
  }

  /**
   * Fallback метод через Web Speech API
   */
  private async startListeningWebSpeech(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.recognition) {
        reject(new Error('Speech recognition not supported'));
        return;
      }

      if (this.isListening) {
        this.stopListening();
      }

      let finalResult = '';

      // Временный обработчик для получения финального результата
      const handleResult = (event: VoiceEvent) => {
        if (event.type === 'result' && event.data.finalTranscript) {
          finalResult = event.data.finalTranscript;
          console.log('Промежуточный результат:', event.data.interimTranscript);
        } else if (event.type === 'end') {
          this.removeEventListener('voiceEvent', handleResult);
          if (finalResult) {
            console.log('Финальный результат распознавания:', finalResult);
            resolve(finalResult);
          } else {
            reject(new Error('Речь не распознана. Попробуйте говорить громче и четче.'));
          }
        } else if (event.type === 'error') {
          this.removeEventListener('voiceEvent', handleResult);
          const errorMessage = event.data?.message || 'Ошибка распознавания речи';
          console.error('Ошибка в handleResult:', errorMessage, event.data);
          reject(new Error(errorMessage));
        }
      };

      this.addEventListener('voiceEvent', handleResult);
      
      try {
        this.recognition.start();
      } catch (error) {
        this.removeEventListener('voiceEvent', handleResult);
        reject(error);
      }
    });
  }

  /**
   * Остановить прослушивание речи
   */
  async stopListening(): Promise<void> {
    if (!this.isListening) {
      return;
    }

    // Если используем Yandex SpeechKit
    if (yandexSpeechService.isSupported() && yandexSpeechService.isRecordingNow()) {
      try {
        // Останавливаем запись и получаем результат
        const text = await yandexSpeechService.stopRecordingAndRecognize({
          language: this.defaultSettings.language,
          format: 'oggopus',
          sampleRateHertz: 16000
        });

        this.isListening = false;
        
        this.emitEvent({
          type: 'end',
          data: { action: 'listening', transcript: text },
          timestamp: new Date()
        });

        // Вызываем resolve из startListening
        if ((this as any)._recognitionResolve) {
          (this as any)._recognitionResolve(text);
          delete (this as any)._recognitionResolve;
          delete (this as any)._recognitionReject;
        }
      } catch (error) {
        this.isListening = false;
        
        this.emitEvent({
          type: 'error',
          data: { 
            error: 'recognition_error', 
            message: error instanceof Error ? error.message : 'Ошибка распознавания речи' 
          },
          timestamp: new Date()
        });

        // Вызываем reject из startListening
        if ((this as any)._recognitionReject) {
          (this as any)._recognitionReject(error instanceof Error ? error : new Error(String(error)));
          delete (this as any)._recognitionResolve;
          delete (this as any)._recognitionReject;
        } else {
          throw error;
        }
      }
    } else if (this.recognition) {
      // Fallback на Web Speech API
      this.recognition.stop();
      this.isListening = false;
    }
  }

  /**
   * Озвучить текст через Yandex TTS
   */
  private async speakYandex(text: string, settings: VoiceSettings): Promise<void> {
    try {
      // Останавливаем предыдущее Yandex TTS аудио если оно есть
      if (this.currentAudio) {
        console.log('⚠️ Останавливаем предыдущее Yandex TTS аудио');
        this.currentAudio.pause();
        this.currentAudio = null;
      }
      
      // Очищаем предыдущий URL
      if (this.currentAudioUrl) {
        URL.revokeObjectURL(this.currentAudioUrl);
        this.currentAudioUrl = null;
      }
      
      // isSpeaking и событие 'start' уже установлены в speak()
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/speech/synthesize`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          text,
          language: settings.language,
          voice: settings.yandexVoice || 'alena',
          emotion: settings.yandexEmotion || 'neutral',
          speed: settings.yandexSpeed || 1.0,
        }),
      });

      if (!response.ok) {
        throw new Error(`Ошибка Yandex TTS: ${response.status}`);
      }

      // Читаем статистику из заголовков
      const charactersUsed = response.headers.get('X-TTS-Characters-Used');
      const estimatedCost = response.headers.get('X-TTS-Estimated-Cost');
      const responseTime = response.headers.get('X-TTS-Response-Time');
      
      if (charactersUsed && estimatedCost && responseTime) {
        console.log(`💰 Yandex TTS статистика: ${charactersUsed} символов, ~${parseFloat(estimatedCost).toFixed(4)} ₽, ${responseTime}мс`);
      }

      // Получаем аудио blob
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Воспроизводим через HTML5 Audio
      const audio = new Audio(audioUrl);
      audio.volume = settings.volume;
      
      // Сохраняем ссылку для возможности остановки
      this.currentAudio = audio;
      this.currentAudioUrl = audioUrl;
      
      return new Promise((resolve, reject) => {
        const cleanup = () => {
          this.isSpeaking = false;
          this.currentAudio = null;
          if (this.currentAudioUrl) {
            URL.revokeObjectURL(this.currentAudioUrl);
            this.currentAudioUrl = null;
          }
        };

        // Естественное завершение
        audio.onended = () => {
          cleanup();
          this.emitEvent({
            type: 'end',
            data: { 
              action: 'speaking', 
              engine: 'yandex',
              stats: {
                charactersUsed: parseInt(charactersUsed || '0'),
                estimatedCost: parseFloat(estimatedCost || '0'),
                responseTime: parseInt(responseTime || '0'),
              }
            },
            timestamp: new Date()
          });
          resolve();
        };

        // Остановка пользователем (ВАЖНО!)
        audio.onpause = () => {
          console.log('🔴 Audio paused - завершаем Promise');
          cleanup();
          this.emitEvent({
            type: 'end',
            data: { action: 'speaking', stopped: true, engine: 'yandex' },
            timestamp: new Date()
          });
          resolve(); // Завершаем Promise при паузе
        };

        // Ошибка воспроизведения
        audio.onerror = (error) => {
          cleanup();
          this.emitEvent({
            type: 'error',
            data: { error: 'yandex_tts_error', message: 'Ошибка воспроизведения аудио' },
            timestamp: new Date()
          });
          reject(new Error('Ошибка воспроизведения аудио'));
        };

        // Запускаем воспроизведение
        audio.play()
          .then(() => {
            console.log('🔊 Yandex TTS начал воспроизведение');
          })
          .catch(error => {
            cleanup();
            reject(error);
          });
      });
    } catch (error) {
      this.isSpeaking = false;
      this.currentAudio = null;
      this.emitEvent({
        type: 'error',
        data: { 
          error: 'yandex_tts_error', 
          message: error instanceof Error ? error.message : 'Ошибка Yandex TTS' 
        },
        timestamp: new Date()
      });
      throw error;
    }
  }

  /**
   * Озвучить текст через браузерный TTS
   */
  private async speakBrowser(text: string, settings: VoiceSettings): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!('speechSynthesis' in window)) {
        reject(new Error('Speech synthesis not supported'));
        return;
      }

      // Агрессивно останавливаем любое текущее озвучивание
      if (speechSynthesis.speaking || speechSynthesis.pending) {
        console.log('⚠️ Останавливаем предыдущее браузерное озвучивание');
        speechSynthesis.cancel();
      }

      // Проверяем доступные голоса
      const voices = speechSynthesis.getVoices();
      if (voices.length === 0) {
        console.warn('⚠️ Нет доступных голосов для браузерного TTS');
        // Пробуем подождать загрузки голосов
        speechSynthesis.addEventListener('voiceschanged', () => {
          const voicesAfterLoad = speechSynthesis.getVoices();
          console.log(`📢 Загружено голосов: ${voicesAfterLoad.length}`);
        }, { once: true });
      }

      this.synthesis = new SpeechSynthesisUtterance(text);
      this.synthesis.lang = settings.language;
      this.synthesis.rate = settings.rate;
      this.synthesis.pitch = settings.pitch;
      this.synthesis.volume = settings.volume;

      // Выбираем голос если указан, иначе пытаемся найти подходящий
      if (settings.voice) {
        this.synthesis.voice = settings.voice;
      } else if (voices.length > 0) {
        // Ищем русский голос если язык русский
        const russianVoice = voices.find(v => v.lang.startsWith('ru'));
        if (russianVoice && settings.language.startsWith('ru')) {
          this.synthesis.voice = russianVoice;
          console.log(`🎤 Выбран голос: ${russianVoice.name} (${russianVoice.lang})`);
        }
      }

      this.synthesis.onstart = () => {
        // isSpeaking и событие 'start' уже установлены в speak()
        console.log('🔊 Браузерный TTS начал воспроизведение');
      };

      this.synthesis.onend = () => {
        this.isSpeaking = false;
        this.emitEvent({
          type: 'end',
          data: { action: 'speaking', engine: 'browser' },
          timestamp: new Date()
        });
        resolve();
      };

      this.synthesis.onerror = (event) => {
        // "interrupted" - это нормальная остановка пользователем, не ошибка
        if (event.error === 'interrupted' || event.error === 'canceled') {
          console.log('🛑 Озвучивание остановлено пользователем');
          this.isSpeaking = false;
          resolve(); // Нормальное завершение
          return;
        }
        
        // Реальная ошибка
        this.isSpeaking = false;
        const errorMessage = `Speech synthesis failed: ${event.error || 'unknown error'}`;
        console.error('❌ Ошибка браузерного TTS:', event.error);
        
        // Останавливаем все озвучивание при ошибке
        speechSynthesis.cancel();
        
        this.emitEvent({
          type: 'error',
          data: { error: 'speech_synthesis_error', message: errorMessage, originalError: event.error },
          timestamp: new Date()
        });
        reject(new Error(errorMessage));
      };
      
      // Небольшая задержка для надежности (известная проблема Chrome)
      setTimeout(() => {
        if (this.synthesis) {
          speechSynthesis.speak(this.synthesis);
        }
      }, 100);
    });
  }

  /**
   * Озвучить текст (автоматически выбирает движок)
   */
  async speak(text: string, settings?: Partial<VoiceSettings>): Promise<void> {
    // Останавливаем предыдущее озвучивание
    if (this.isSpeaking) {
      console.log('⚠️ Уже идёт озвучивание, останавливаем...');
      this.stopSpeaking();
      // Ждём немного, чтобы очистка завершилась
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // ВАЖНО: Устанавливаем флаг ДО начала озвучивания
    this.isSpeaking = true;

    const currentSettings = { ...this.defaultSettings, ...settings };
    
    console.log(`🔊 TTS: движок="${currentSettings.ttsEngine}", язык="${currentSettings.language}"`);

    // Отправляем событие старта сразу
    this.emitEvent({
      type: 'start',
      data: { action: 'speaking', engine: currentSettings.ttsEngine },
      timestamp: new Date()
    });

    try {
      // Выбираем движок на основе настроек
      if (currentSettings.ttsEngine === 'yandex') {
        return await this.speakYandex(text, currentSettings);
      } else {
        return await this.speakBrowser(text, currentSettings);
      }
    } catch (error) {
      // Проверяем, не является ли это нормальной остановкой пользователем
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isUserInterruption = errorMessage.includes('interrupted') || errorMessage.includes('canceled');
      
      // При ошибке сбрасываем флаг
      this.isSpeaking = false;
      
      // Только для реальных ошибок (не прерываний пользователем)
      if (!isUserInterruption) {
        // Останавливаем все возможные воспроизведения
        speechSynthesis.cancel();
        if (this.currentAudio) {
          this.currentAudio.pause();
          this.currentAudio = null;
        }
        
        this.emitEvent({
          type: 'error',
          data: { action: 'speaking', error: errorMessage },
          timestamp: new Date()
        });
      }
      
      throw error;
    }
  }

  /**
   * Остановить озвучивание
   */
  stopSpeaking(): void {
    console.log('🛑 Остановка озвучивания (все движки)');
    
    const wasSpeaking = this.isSpeaking;
    this.isSpeaking = false;
    
    // Останавливаем браузерный TTS (агрессивно)
    if ('speechSynthesis' in window) {
      if (speechSynthesis.speaking || speechSynthesis.pending) {
        console.log('🔴 Останавливаем браузерный TTS');
        speechSynthesis.cancel();
      }
    }
    
    // Очищаем SpeechSynthesisUtterance
    if (this.synthesis) {
      this.synthesis = null;
    }
    
    // Останавливаем Yandex TTS (HTML5 Audio)
    if (this.currentAudio) {
      console.log('🔴 Останавливаем Yandex TTS аудио');
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    
    // Очищаем URL объекта
    if (this.currentAudioUrl) {
      URL.revokeObjectURL(this.currentAudioUrl);
      this.currentAudioUrl = null;
    }
    
    // Отправляем событие остановки только если действительно что-то останавливали
    if (wasSpeaking) {
      this.emitEvent({
        type: 'end',
        data: { action: 'speaking', stopped: true },
        timestamp: new Date()
      });
    }
  }

  /**
   * Получить список доступных голосов (только русские)
   */
  getAvailableVoices(): SpeechSynthesisVoice[] {
    if ('speechSynthesis' in window) {
      return speechSynthesis.getVoices().filter(voice => 
        voice.lang.startsWith('ru')
      );
    }
    return [];
  }

  /**
   * Проверить поддержку голосовых функций
   */
  isSupported(): { recognition: boolean; synthesis: boolean } {
    return {
      // Yandex SpeechKit работает через сервер (нужен только MediaRecorder)
      recognition: yandexSpeechService.isSupported() || !!(window.SpeechRecognition || window.webkitSpeechRecognition),
      synthesis: 'speechSynthesis' in window
    };
  }

  /**
   * Получить текущие настройки
   */
  getSettings(): VoiceSettings {
    return { ...this.defaultSettings };
  }

  /**
   * Обновить настройки
   */
  updateSettings(settings: Partial<VoiceSettings>): void {
    this.defaultSettings = { ...this.defaultSettings, ...settings };
    this.saveSettings();
    
    // Обновляем настройки распознавания речи
    if (this.recognition && settings.language) {
      this.recognition.lang = settings.language;
    }
  }

  /**
   * Получить статус сервиса
   */
  getStatus(): { listening: boolean; speaking: boolean } {
    return {
      listening: this.isListening,
      speaking: this.isSpeaking
    };
  }

  // Управление событиями

  /**
   * Добавить обработчик событий
   */
  addEventListener(eventType: string, callback: (event: VoiceEvent) => void): void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, []);
    }
    this.eventListeners.get(eventType)!.push(callback);
  }

  /**
   * Удалить обработчик событий
   */
  removeEventListener(eventType: string, callback: (event: VoiceEvent) => void): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  /**
   * Отправить событие
   */
  private emitEvent(event: VoiceEvent): void {
    // Отправляем событие в канал по типу события (start, end, error)
    const listeners = this.eventListeners.get(event.type);
    if (listeners) {
      console.log(`📢 Отправка события "${event.type}" подписчикам (${listeners.length} слушателей)`);
      listeners.forEach(callback => callback(event));
    } else {
      console.warn(`⚠️ Нет подписчиков на событие "${event.type}"`);
    }
  }

  // Работа с настройками

  /**
   * Загрузить настройки из localStorage
   */
  private loadSettings(): void {
    try {
      const saved = localStorage.getItem('voiceSettings');
      if (saved) {
        const settings = JSON.parse(saved);
        this.defaultSettings = { ...this.defaultSettings, ...settings };
      }
    } catch (error) {
      console.error('Error loading voice settings:', error);
    }
  }

  /**
   * Сохранить настройки в localStorage
   */
  private saveSettings(): void {
    try {
      localStorage.setItem('voiceSettings', JSON.stringify(this.defaultSettings));
    } catch (error) {
      console.error('Error saving voice settings:', error);
    }
  }

  /**
   * Сбросить настройки к значениям по умолчанию
   */
  resetSettings(): void {
    this.defaultSettings = {
      language: 'ru-RU',
      voice: null,
      pitch: 1,
      rate: 1,
      volume: 1,
      ttsEngine: 'browser',
      autoSpeak: false,
      yandexVoice: 'alena',
      yandexEmotion: 'neutral',
      yandexSpeed: 1.0,
    };
    this.saveSettings();
  }
}

// Экспортируем единственный экземпляр сервиса
export default new VoiceService();
